import { promiseSerial } from '@iotize/common/promise';
import { Observable, Subject } from 'rxjs';
import { share, tap } from 'rxjs/operators';
import { debug } from './debug';
const TAG = 'StepOperations';
/**
 * Step operation execution manager
 *
 * Template type T: the event type. It's the return type of each operations
 * Template type DataType: context additional DataType
 * @experimental
 */
export class StepOperations {
    get isCancelled() {
        return this._state === StepOperations.State.CANCELED;
    }
    get isCompleted() {
        return this._state === StepOperations.State.COMPLETED;
    }
    get state() {
        return this._state;
    }
    get events() {
        return this._events.asObservable();
    }
    set taskErrorHandler(errorHandler) {
        this._taskErrorHandler = errorHandler;
    }
    get operations() {
        return this.operationContainers.map((container) => container.operation);
    }
    constructor(operationContainers) {
        this.operationContainers = operationContainers;
        this._state = StepOperations.State.PENDING;
        this._events = new Subject();
    }
    static createSequentialContainers(operations) {
        return operations.map((op, index) => {
            if (typeof op === 'function') {
                const opFct = op;
                op = new (class {
                    perform(observer, context) {
                        return opFct(observer, context);
                    }
                })();
            }
            else if (op instanceof Observable) {
                const opObs = op.pipe(share());
                op = new (class {
                    async perform(observer, context) {
                        return await opObs
                            .pipe(tap((event) => {
                            observer.next(event);
                        }))
                            .toPromise();
                    }
                })();
            }
            return {
                operation: op,
                index: index,
            };
        });
    }
    static sequential(operations) {
        const containers = StepOperations.createSequentialContainers(operations);
        return new StepOperations(containers);
    }
    runEvents() {
        if (!this._currentExecutionEvents) {
            this._currentExecutionEvents = new Subject();
        }
        return this._currentExecutionEvents.asObservable();
    }
    /**
     * Reset as if operations were never executed.
     * Will call onReset callback for each Operation if defined.
     * @returns this
     */
    reset() {
        debug(TAG, 'reset()');
        this.operationContainers.forEach((container) => {
            if (container.operation.onReset) {
                container.operation.onReset();
            }
        });
        this._currentExecutionEvents = undefined;
        this.operationContainers.forEach((op) => {
            op.done = undefined;
            op.error = undefined;
            op.result = undefined;
        });
        this._state = StepOperations.State.PENDING;
        return this;
    }
    /**
     * Will retry from the last failed operation.
     * Call 'onRetry' callback for each Operation if definied.
     *
     * If all operations where successfully executed, it will do nothing.
     *
     * Call reset() first if you want to run operations again.
     */
    retry() {
        debug(TAG, 'retry()');
        this._state = StepOperations.State.PENDING;
        this.operationContainers.forEach((container) => {
            if (container.operation.onRetry) {
                container.operation.onRetry();
            }
        });
        return this.run();
    }
    /**
     * Execute operations
     * If it already running, do nothing
     *
     */
    run() {
        debug(TAG, 'run()');
        if (!this._currentExecutionEvents) {
            debug(TAG, 'create _currentExecutionEvents');
            this._currentExecutionEvents = new Subject();
            this._currentExecutionEvents.subscribe({
                next: (event) => {
                    this._events.next({
                        type: 'event',
                        event: event,
                    });
                },
                error: (err) => {
                    this._events.next({
                        type: 'error',
                        event: err,
                    });
                },
                complete: () => {
                    this._events.next({
                        type: 'complete',
                    });
                },
            });
        }
        switch (this._state) {
            case StepOperations.State.PENDING:
                this._start(this._currentExecutionEvents);
                break;
        }
        return this._currentExecutionEvents.asObservable();
    }
    /**
     * Cancel running operations
     */
    cancel() {
        debug(TAG, 'cancel()');
        this._state = StepOperations.State.CANCELED;
    }
    /**
     * Transform this operation as an observable
     * Subscribe to observable to run operation
     * Unsubscribe to cancel operation
     *
     * Each time you call this method, a new observable is created
     */
    asObservable() {
        // TODO maybe we should check if tak a
        return new Observable((emitter) => {
            debug(TAG, 'StepOperation converted to observable has been subscribed');
            this.run().subscribe(emitter);
            return () => {
                if (this._state === StepOperations.State.RUNNING) {
                    debug(TAG, 'StepOperation converted to observable has been cancelled');
                    this.cancel();
                }
            };
        }).pipe(share());
    }
    // subscribe(observer?: PartialObserver<T>): Subscription {
    //     return this.runEvents().subscribe(observer);
    // }
    checkIsCancelled() {
        if (this.isCancelled) {
            throw StepOperationsError.cancel();
        }
    }
    checkIsRunning() {
        this.checkIsCancelled();
        if (this._state !== StepOperations.State.RUNNING) {
            throw StepOperationsError.unknown(`Operation should not have been executed`);
        }
    }
    _onTaskError(err, context) {
        if (this._taskErrorHandler) {
            debug('custom error handler');
            this._taskErrorHandler.handle(err, context);
        }
        else {
            throw err;
        }
    }
    // private createExecStream() {
    //     debug(TAG, 'createExecStream');
    //     return new Subject();
    //     (observer: Observer<T>) => {
    //         switch (this._state){
    //             case StepOperations.State.PENDING:
    //                 this._start(observer);
    //         }
    //         if (this._state === StepOperations.State.PENDING){
    //         }
    //         return () => {
    //             debug(TAG, 'unsubsribed');
    //             // this._ignoreError = true;
    //             // this._state = StepOperations.State.CANCELED;
    //         }
    //     }).pipe(share());
    // }
    async _start(observer) {
        this._state = StepOperations.State.RUNNING;
        const operationsToPerform = this.operationContainers.filter((op) => {
            return op.done !== true;
        });
        debug(TAG, '_start', `operation number: ${operationsToPerform.length}`);
        try {
            const data = {};
            // for (let task of operationsToPerform){
            //     debug(TAG, `Task n°${task.index} (${index + 1}/${operationsToPerform.length})${task.operation.alias ? `[alias=${task.operation.alias}]`: ''}: Start processing...`);
            //     this.checkIsCancelled();
            //     let context: StepOperations.Context<DataType> = {
            //         position: index,
            //         total: operationsToPerform.length,
            //         index: task.index,
            //         prevOperation: index > 0 ? this.operationContainers[index - 1] : undefined,
            //         data: data
            //     };
            //     try {
            //         await task.operation.perform(observer, context);
            //         let res = await task.operation.perform(observer, context);
            //         task.done = true;
            //         task.result = res;
            //         return res;
            //     }
            //     catch (err){
            //         task.error = err;
            //         this._onTaskError(err, context)
            //     }
            //     debug(TAG, `Task n°${task.index} (${index + 1}/${operationsToPerform.length}): DONE!`);
            //     index++;
            // }
            await promiseSerial(operationsToPerform, async (task, index) => {
                const context = {
                    position: index,
                    total: operationsToPerform.length,
                    index: task.index,
                    prevOperation: index > 0 ? this.operationContainers[index - 1] : undefined,
                    data: data,
                };
                try {
                    this.checkIsRunning();
                    debug(TAG, `Task n°${task.index + 1} (${index + 1}/${operationsToPerform.length}): Start processing...`);
                    const res = await task.operation.perform(observer, context);
                    task.done = true;
                    task.result = res;
                    // observer.next(res);
                    debug(TAG, `Task n°${task.index + 1} (${index + 1}/${operationsToPerform.length}): DONE!`);
                    return res;
                }
                catch (untypedError) {
                    const err = untypedError;
                    task.error = err;
                    debug(TAG, `Task n°${task.index + 1} (${index + 1}/${operationsToPerform.length}): error: ${err.message}`);
                    this._onTaskError(err, context);
                }
            });
            debug(TAG, 'complete');
            this.onComplete(observer);
        }
        catch (err) {
            this.onError(err, observer);
        }
    }
    onComplete(observer) {
        observer.complete();
        this._state = StepOperations.State.COMPLETED;
    }
    onError(err, observer) {
        debug(TAG, 'error ', err.message);
        this._state = StepOperations.State.ERRORED;
        observer.error(err);
        this._currentExecutionEvents = undefined;
    }
}
export class StepOperationsError extends Error {
    constructor(code, msg) {
        super(msg);
        this.code = code;
    }
    static cancel() {
        return new StepOperationsError(StepOperationsError.Code.STEP_OPERATION_CANCEL, `Operation cancel by user`);
    }
    static unknown(msg) {
        return new StepOperationsError(StepOperationsError.Code.UNKNOWN_ERROR, msg);
    }
}
(function (StepOperationsError) {
    let Code;
    (function (Code) {
        Code["UNKNOWN_ERROR"] = "STEP_OPERATION_UNKNOWN_ERROR";
        Code["STEP_OPERATION_CANCEL"] = "STEP_OPERATION_CANCEL";
    })(Code = StepOperationsError.Code || (StepOperationsError.Code = {}));
})(StepOperationsError || (StepOperationsError = {}));
(function (StepOperations) {
    let State;
    (function (State) {
        State["PENDING"] = "PENDING";
        State["RUNNING"] = "RUNNING";
        State["COMPLETED"] = "COMPLETED";
        State["CANCELED"] = "CANCELED";
        State["ERRORED"] = "ERRORED";
    })(State = StepOperations.State || (StepOperations.State = {}));
})(StepOperations || (StepOperations = {}));
export class IgnoreErrorStrategy {
}
